import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import TeamSearchSelector from '../../components/TeamSearchSelector';
import PlayerSearchSelector from '../../components/PlayerSearchSelector';
import EditPlayersButton from '../../../components/players/edit-players-button';
import ErrorAlert from '../../components/ErrorAlert';
import { useAdminData } from '../../../contexts/AdminDataContext';
import { apiGet, apiPost, apiPut } from '../../../services/apiFetch';
import '../../../styles/CreateMatchForm.css';
import SettleMatchComponent, { formatGameDetailsForSubmission } from '../../components/SettleMatchComponent';

// Material-UI Date Picker imports
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const MatchForm = () => {
  const { id, date } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isEditing = !!id;
  const { allTeams, allLeagues, teamsLoading, leaguesLoading } = useAdminData();

  // States for form data
  const [formData, setFormData] = useState({
    date: '',
    time: '',
    team_1_id: '',
    team_2_id: '',
    league_id: '',
    match_format: {
      type: 'bestOf',
      count: 5
    },
    stream_links: [''],
    source_url: ''
  });

  const [teams, setTeams] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [team1Info, setTeam1Info] = useState(null);
  const [team2Info, setTeam2Info] = useState(null);
  const [status, setStatus] = useState('upcoming');

  // States for completed matches
  const [games, setGames] = useState([
    {
      game_number: 1,
      duration: 300,
      map_name: 'DFH Stadium',
      winner: null
    }
  ]);

  // States for player stats
  const [team1Players, setTeam1Players] = useState([]);
  const [team2Players, setTeam2Players] = useState([]);
  const [playerStats, setPlayerStats] = useState({});

  const [loading, setLoading] = useState(isEditing);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Add state for settlement data
  const [settlementData, setSettlementData] = useState(null);

  // Set teams and leagues from context
  useEffect(() => {
    if (!teamsLoading && !leaguesLoading) {
      setTeams(allTeams);
      setLeagues(allLeagues);
    }
  }, [allTeams, allLeagues, teamsLoading, leaguesLoading]);

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {

        // If editing, fetch match data
        let matchData;
        let matchStatus;
        if (isEditing) {
          if (location.state?.matchData) {
            console.log("MatchForm.js: Using passed match data from AdminMatchDetails.js", location.state.matchData);
            matchData = location.state.matchData;
            setStatus(location.state.matchStatus);
            matchStatus = location.state.matchStatus;
          } else {
            const { data: matchJson, error } = await apiGet(`/api/rl/matches/upcoming/${id}?date=${date}`);
            if (error) throw new Error(error.error);

            matchData = matchJson.match;
          }
          const teamData = matchData.teams;
          // Set form data
          const newForm = {
            ...matchData,
            league_id: matchData.league_details.id,
            old_date: matchData.date,
          };

          // Set selected teams
          const team1 = teamData.find(t => t.designation === 1);
          if (team1) {
            newForm.team_1_id = team1.id
            newForm.team1Info = {
              id: team1.id,
              name: team1.name,
              image: team1.image,
              player_ids: team1.players.map(p => p.id),
            }
            setTeam1Players(team1.players)
            setTeam1Info(newForm.team1Info)
            setTeams(prev => prev.filter(t => t.id !== team1.id))
          }

          const team2 = teamData.find(t => t.designation === 2);
          if (team2) {
            newForm.team_2_id = team2.id
            newForm.team2Info = {
              id: team2.id,
              name: team2.name,
              image: team2.image,
              player_ids: team2.players.map(p => p.id),
            }
            setTeam2Players(team2.players)
            setTeam2Info(newForm.team2Info)
            setTeams(prev => prev.filter(t => t.id !== team2.id))
          }
          setFormData(newForm);
          if (matchStatus === 'completed' && matchData.metadata?.games) {
            setGames(createExistingGames(matchData.metadata.games, team1, team2));
            setPlayerStats(createExistingPlayerStats(matchData));
          }
        }
      } catch (error) {
        console.error("Error fetching initial data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, date, isEditing]);

  const createExistingPlayerStats = (matchData) => {
    const playerStats = {};
    matchData.teams.forEach(team => {
      team.players.forEach(player => {
        playerStats[`${team.id}-${player.id}`] = {};
        const playerResults = player.results;
        playerResults.forEach(result => {
          const gameNumber = result.game_number;
          playerStats[`${team.id}-${player.id}`][gameNumber] = {
            score: result.score,
            goals: result.goals,
            assists: result.assists,
            saves: result.saves,
            shots: result.shots,
            total_distance: result.total_distance,
          };
        });
      });
    });
    return playerStats;
  }

  const createExistingGames = (games, team1, team2) => {
    const existingGames = [];
    const team1Results = team1.results;
    const team2Results = team2.results;
    games.forEach(game => {
      const team1GameResult = team1Results.find(result => result.game_number === game.game_number);
      const team2GameResult = team2Results.find(result => result.game_number === game.game_number);
      const newGame = {
        game_number: game.game_number,
        duration: game.duration,
        map_name: game.map_name,
        winner: team1GameResult.result > team2GameResult.result ? 'team1' : 'team2'
      }
      existingGames.push(newGame);
    });
    return existingGames;
  }

  // Fetch team players
  const fetchTeam1Players = async (teamId) => {
    try {
      const { data: teamJson, error: teamErr } = await apiGet(`/api/rl/teams/${teamId}`);
      if (teamErr) throw new Error(teamErr.error);
      const player_ids = teamJson.team.player_ids;
      const { data: playersJson, error: playersErr } = await apiGet(`/api/rl/teams/${teamId}/players?image=true&name=true&player_ids=${player_ids}`);
      if (playersErr) throw new Error(playersErr.error);
      setTeam1Players(playersJson.players);
      initializePlayerStats(teamJson.team);
    } catch (error) {
      console.error("Error fetching team 1 players:", error);
    }
  };

  const fetchTeam2Players = async (teamId) => {
    try {
      const { data: teamJson, error: teamErr } = await apiGet(`/api/rl/teams/${teamId}`);
      if (teamErr) throw new Error(teamErr.error);
      const player_ids = teamJson.team.player_ids;
      const { data: playersJson, error: playersErr } = await apiGet(`/api/rl/teams/${teamId}/players?image=true&name=true&player_ids=${player_ids}`);
      if (playersErr) throw new Error(playersErr.error);
      setTeam2Players(playersJson.players);
      initializePlayerStats(teamJson.team);
    } catch (error) {
      console.error("Error fetching team 2 players:", error);
    }
  };

  // Initialize player stats
  const initializePlayerStats = (team) => {
    const initialStats = {};

    team.player_ids.forEach(player_id => {
      const key = `${team.id}-${player_id}`;
      initialStats[key] = {};

      games.forEach(game => {
        initialStats[key][game.game_number] = {
          score: null,
          goals: null,
          assists: null,
          saves: null,
          shots: null,
          total_distance: null
        };
      });
    });

    setPlayerStats(prev => ({ ...prev, ...initialStats }));
  };

  // Handle team selection
  const handleTeam1Select = (team) => {
    setTeam1Info(team);
    setTeams(prev =>
      prev
        ? prev.filter(t => t.id !== team?.id)
        : []
    );
    setFormData({
      ...formData,
      team_1_id: team ? team.id : '',
      team1Info: team
    });
    if (team) {
      fetchTeam1Players(team.id);
    } else {
      setTeam1Players([]);
    }
  };

  const handleTeam2Select = (team) => {
    setTeam2Info(team);
    setTeams(prev =>
      prev
        ? prev.filter(t => t.id !== team?.id)
        : []
    );
    setFormData({
      ...formData,
      team_2_id: team ? team.id : '',
      team2Info: team
    });
    if (team) {
      fetchTeam2Players(team.id);
    } else {
      setTeam2Players([]);
    }
  };

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  // Handle date picker change
  const handleDateChange = (date) => {
    if (date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;
      setFormData({
        ...formData,
        date: formattedDate
      });
    } else {
      setFormData({
        ...formData,
        date: ''
      });
    }
  };

  // Handle time picker change
  const handleTimeChange = (time) => {
    if (time) {
      const formattedTime = time.toTimeString().split(' ')[0]; // Format as HH:MM:SS
      setFormData({
        ...formData,
        time: formattedTime
      });
    } else {
      setFormData({
        ...formData,
        time: ''
      });
    }
  };

  // Handle stream link change
  const handleStreamLinkChange = (index, value) => {
    const updatedLinks = [...formData.stream_links];
    updatedLinks[index] = value;
    setFormData({
      ...formData,
      stream_links: updatedLinks
    });
  };

  // Add/remove stream link
  const addStreamLink = () => {
    setFormData({
      ...formData,
      stream_links: [...formData.stream_links, '']
    });
  };

  const removeStreamLink = (index) => {
    const updatedLinks = [...formData.stream_links];
    updatedLinks.splice(index, 1);
    setFormData({
      ...formData,
      stream_links: updatedLinks
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      // Prepare submission data
      const submissionData = {
        ...formData,
        gameid: id
      };


      if (isEditing && status !== 'completed') {
        const { data, error: updateErr } = await apiPut(`/api/rl/matches/upcoming/${id}`, submissionData);
        if (updateErr) {
          setError(updateErr);
          return;
        }
      } else {
        const { data, error: createErr } = await apiPost('/api/rl/matches/upcoming', submissionData);
        if (createErr) {
          setError(createErr);
          return;
        }
        console.log("MatchForm.js: createUpcomingMatch response", data);
      }

      // If status is completed and we have settlement data, include it
      if (status === 'completed' && settlementData) {
        console.log("handleSubmit settlementData", settlementData);
        const gameDetails = formatGameDetailsForSubmission(
          settlementData.games,
          settlementData.team1Players,
          settlementData.team2Players,
          settlementData.playerStats,
          settlementData.team1Info,
          settlementData.team2Info
        );

        // Add settlement data to your submission
        const settlementSubmissionData = {
          matchid: id,
          date: formData.date,
          gameDetails: gameDetails,
          ...formData
        };

        console.log("settlementSubmissionData", settlementSubmissionData);


        // Make the settlement API call
        const { data: settlementResult, error: settlementErr } = await apiPost(`/api/rl/matches/${id}/complete`, settlementSubmissionData);
        console.log("Settle match result:", settlementResult);
        if (settlementErr) {
          setError(settlementErr);
          return; // Don't navigate if settlement failed
        }
      }

      navigate('/admin/matches');
    } catch (error) {
      console.error("Error submitting match:", error);
      setError({
        error: 'internal_error',
        message: error.message || 'Error submitting match. Please try again.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  if (loading) {
    return <div className="loading-spinner"></div>;
  }

  return (
    <div className="player-form-page">
      <div className="page-header">
        <button type="button" className="back-button" onClick={() => navigate(-1)} style={{marginLeft: 16}}>
          ← Back
        </button>
        <h1>{isEditing ? 'Edit Match' : 'Create Match'}</h1>
      </div>

      <form onSubmit={handleSubmit} className="admin-form">
        <div className="form-section">
          <h2>Match Details</h2>

          <div className="form-row">
            <div className="form-group">
              <label>Match Status</label>
              <input
                type="text"
                name="status"
                value={status.charAt(0).toUpperCase() + status.slice(1)}
                disabled
              />
            </div>

            <div className="form-group">
              <label>Date (Please input UTC date)</label>
              <div className="date-time-picker-container">
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    value={formData.date ? new Date(formData.date) : null}
                    onChange={handleDateChange}
                    slotProps={{
                      textField: {
                        size: 'small',
                        variant: 'outlined',
                        fullWidth: true,
                        required: true,
                        placeholder: 'Select date...'
                      }
                    }}
                  />
                </LocalizationProvider>
                {formData.date && (
                  <button
                    type="button"
                    className="picker-clear-btn"
                    onClick={() => handleDateChange(null)}
                    title="Clear date"
                    aria-label="Clear selected date"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>

            <div className="form-group">
              <label>Time (Please input UTC time)</label>
              <div className="date-time-picker-container">
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <TimePicker
                    value={formData.time ? new Date(`1970-01-01T${formData.time}`) : null}
                    onChange={handleTimeChange}
                    ampm={false}
                    views={['hours', 'minutes', 'seconds']}
                    slotProps={{
                      textField: {
                        size: 'small',
                        variant: 'outlined',
                        fullWidth: true,
                        required: true,
                        placeholder: 'Select time...'
                      }
                    }}
                  />
                </LocalizationProvider>
                {formData.time && (
                  <button
                    type="button"
                    className="picker-clear-btn"
                    onClick={() => handleTimeChange(null)}
                    title="Clear time"
                    aria-label="Clear selected time"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>League</label>
              <select
                name="league_id"
                value={formData.league_id}
                onChange={handleInputChange}
                required
              >
                <option value="">Select League</option>
                {leagues.map(league => (
                  <option key={league.id} value={league.id}>
                    {league.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Format Type</label>
              <select
                name="match_format.type"
                value={formData.match_format.type}
                onChange={handleInputChange}
                required
              >
                <option value="bestOf">Best Of</option>
                <option value="firstTo">First To</option>
              </select>
            </div>

            <div className="form-group">
              <label>Format Count</label>
              <select
                name="match_format.count"
                value={formData.match_format.count}
                onChange={handleInputChange}
                required
              >
                <option value="3">3</option>
                <option value="5">5</option>
                <option value="7">7</option>
              </select>
            </div>

            <div className="form-group">
              <label>Source URL</label>
              <input
                type="url"
                name="source_url"
                value={formData.source_url || ''}
                onChange={handleInputChange}
                placeholder="https://example.com"
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>Teams</h2>

          <div className="team-selection vertical">
            <div className="team-selector">
              <label>Team 1 (Blue)</label>
              <TeamSearchSelector
                placeholder="Search for team..."
                items={teams}
                onSelect={handleTeam1Select}
                setTeams={setTeams}
                initialItem={team1Info}
              />
              <table className='data-table player-roster-table'>
                <thead>
                  <tr>
                    <th>Player</th>
                    <th>Username</th>
                  </tr>
                </thead>
                <tbody>
                  {team1Players.length === 0 ? (
                    <tr>
                      <td colSpan="2" className="empty-roster-message">No players on this team yet</td>
                    </tr>
                  ) : (
                    team1Players.map(player => (
                      <tr key={`${team1Info.id}-${player.id}`} className="player-row">
                        <td>
                          {player.image ? (
                            <div className="match-player-info">
                              <img src={player.image} alt={player.username} className="player-avatar" onError={(e) => { e.target.src = '/assets/images/placeholder.png' }} />
                              <span>{player.username}</span>
                            </div>
                          ) : (
                            player.username
                          )}
                        </td>
                        <td><div className="match-player-username">{player.username}</div></td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="team-selector">
              <label>Team 2 (Orange)</label>
              <TeamSearchSelector
                placeholder="Search for team..."
                items={teams}
                onSelect={handleTeam2Select}
                initialItem={team2Info}
                setTeams={setTeams}
              />
              <table className='data-table player-roster-table'>
                <thead>
                  <tr>
                    <th>Player</th>
                    <th>Username</th>
                  </tr>
                </thead>
                <tbody>
                  {team2Players.length === 0 ? (
                    <tr>
                      <td colSpan="2" className="empty-roster-message">No players on this team yet</td>
                    </tr>
                  ) : (
                    team2Players.map(player => (
                      <tr key={`${team2Info.id}-${player.id}`} className="player-row">
                        <td>
                          {player.image ? (
                            <div className="match-player-info">
                              <img src={player.image} alt={player.username} className="player-avatar" onError={(e) => { e.target.src = '/assets/images/placeholder.png' }} />
                              <span>{player.username}</span>
                            </div>
                          ) : (
                            player.username
                          )}
                        </td>
                        <td><div className="match-player-username">{player.username}</div></td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>Stream Links</h2>

          {formData.stream_links.map((link, index) => (
            <div key={index} className="form-row">
              <div className="form-group">
                <input
                  type="url"
                  placeholder="Stream URL"
                  value={link}
                  onChange={(e) => handleStreamLinkChange(index, e.target.value)}
                />
              </div>

              <button
                type="button"
                className="button"
                onClick={() => removeStreamLink(index)}
                disabled={formData.stream_links.length <= 1}
              >
                Remove
              </button>
            </div>
          ))}

          <button
            type="button"
            className="button"
            onClick={addStreamLink}
          >
            Add Stream Link
          </button>
        </div>
        {status === 'completed' && (
          <SettleMatchComponent
            matchData={formData}
            initialGames={games}
            initialPlayerStats={playerStats}
            onDataChange={setSettlementData}
            isEditing={isEditing}
          />
        )}

        <ErrorAlert
          error={error}
          onDismiss={() => setError(null)}
          title="Error Creating/Updating Match"
        />

        <div className="form-actions">
          <button
            type="button"
            className="cancel-button"
            onClick={handleCancel}
          >
            Cancel
          </button>

          <button
            type="submit"
            className="submit-button"
            disabled={submitting}
          >
            {submitting ? 'Saving...' : (isEditing ? 'Update Match' : 'Create Match')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default MatchForm;
